import { toast } from "react-toastify";
import { getCurrencyDecimals, getCurrencySymbol, getTenantCurrency } from "../tenant/currency";


const handleCopy = (text: string) => {
	if (!text) return;
	if (navigator.clipboard) {
		navigator.clipboard.writeText(text);
	} else {
		const textArea = document.createElement("textarea");
		textArea.value = text;
		textArea.style.position = "fixed";
		textArea.style.left = "-9999px";
		document.body.appendChild(textArea);
		textArea.select();
		document.execCommand("copy");
		document.body.removeChild(textArea);
	}
	toast.success("Copied to clipboard");
};

const formatNumber = (num: number | string) => {
	if (num === "-") return "-";
	return num.toString().replace(/\d(?=(\d{3})+\.)/g, '$&,');
};

export const formatTimestamp = (timestampString: string): string => {
	try {
	  if (!timestampString) {
		return '';
	  }
  
	  const date = new Date(timestampString);
	  
	  if (isNaN(date.getTime())) {
		return '';
	  }
  
	  const options: Intl.DateTimeFormatOptions = {
		month: 'short',       // "Mar"
		day: 'numeric',       // "21"
		year: 'numeric',      // "2025"
		hour: 'numeric',      // "10"
		minute: '2-digit',    // "45"
		hour12: true          // Use AM/PM
	  };
	  
	  return date.toLocaleString('en-US', options);
	} catch (error) {
	  console.error('Error formatting date:', error);
	  return '';
	}
  };

export const getInitials = (input: string): string => {
	const words = input.trim().split(/\s+/);
	if (words.length === 0) {
		return "";
	}
	const firstWordInitial = words[0].charAt(0).toUpperCase();
	const secondWordInitial = words.length > 1 ? words[1].charAt(0).toUpperCase() : "";
	return firstWordInitial + secondWordInitial;
};

export function formatDate(date?: Date | string): string {
	const options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };
	return date ? new Date(date)?.toLocaleDateString("en-US", options) : "/";
}

function getOrdinalPosition(num: number): string {
	if (num === 0) {
		return "";
	}
	const suffixes = ["th", "st", "nd", "rd"];
	const value = num % 100;
	return num + (suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]);
}

function formatTime(dateString: string): string {
	const date = new Date(dateString);

	let hours = date.getUTCHours();
	const minutes = date.getUTCMinutes();

	const ampm = hours >= 12 ? "PM" : "AM";

	hours = hours % 12;
	hours = hours ? hours : 12;

	const formattedMinutes = minutes < 10 ? "0" + minutes : minutes.toString();

	return `${hours}:${formattedMinutes}${ampm}`;
}

function formatTimeLocal(dateString: string): string {
	const date = new Date(dateString);

	let hours = date.getHours();
	const minutes = date.getMinutes();

	const ampm = hours >= 12 ? "PM" : "AM";

	hours = hours % 12;
	hours = hours ? hours : 12;

	const formattedMinutes = minutes < 10 ? "0" + minutes : minutes.toString();

	return `${hours}:${formattedMinutes}${ampm}`;
}

function convertToString(inputNum: string): string {
	let num = parseFloat(inputNum);
	const roundedNum = Math.round(num * 100) / 100;
	return roundedNum.toLocaleString("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
}

function isGuid(value: any): boolean {
    // Regular expression to match both normalized GUIDs (without dashes) and standard GUIDs (with dashes)
    const guidPattern = /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i;
    return typeof value === 'string' && guidPattern.test(value);
}

function formatEnumString(enumValue: string): string {
	return enumValue.replace(/([a-z])([A-Z])/g, "$1 $2");
  }

/**
 * Calculate the difference between a given date string and the current time
 * Returns a formatted string with years, months, weeks, days, hours, minutes
 * Handles singular/plural forms correctly
 * 
 * @param {string} dateString - The date string to compare with current time
 * @return {string} - Formatted time difference
 */
function calculateTimeDifference(dateString: string): string {
	const startDate = new Date(dateString);
	const currentDate = new Date();
	
	if (isNaN(startDate.getTime())) {
	  return "Invalid date format";
	}
	
	let diff = currentDate.getTime() - startDate.getTime();
	
	if (diff < 0) {
	  return "The provided date is in the future";
	}
	
	const { years, months, remainingDiff } = calculateYearsAndMonths(startDate, currentDate);
	
	const timeUnits = calculateTimeUnits(remainingDiff);
	
	return formatTimeDifference({ years, months, ...timeUnits });
  }
  
  /**
   * Calculate years and months between two dates
   */
  function calculateYearsAndMonths(startDate: Date, currentDate: Date) {
	let years = currentDate.getFullYear() - startDate.getFullYear();
	let months = currentDate.getMonth() - startDate.getMonth();
	
	if (months < 0) {
	  years--;
	  months += 12;
	}
	
	if (currentDate.getDate() < startDate.getDate()) {
	  months--;
	  
	  if (months < 0) {
		years--;
		months += 12;
	  }
	}
	
	const tempDate = new Date(startDate);
	tempDate.setFullYear(tempDate.getFullYear() + years);
	tempDate.setMonth(tempDate.getMonth() + months);
	const remainingDiff = currentDate.getTime() - tempDate.getTime();
	
	return { years, months, remainingDiff };
  }
  
  /**
   * Calculate weeks, days, hours, and minutes from milliseconds
   */
  function calculateTimeUnits(diff: number) {
	const millisecondsInMinute = 1000 * 60;
	const millisecondsInHour = millisecondsInMinute * 60;
	const millisecondsInDay = millisecondsInHour * 24;
	const millisecondsInWeek = millisecondsInDay * 7;
	
	const weeks = Math.floor(diff / millisecondsInWeek);
	diff -= weeks * millisecondsInWeek;
	
	const days = Math.floor(diff / millisecondsInDay);
	diff -= days * millisecondsInDay;
	
	const hours = Math.floor(diff / millisecondsInHour);
	diff -= hours * millisecondsInHour;
	
	const minutes = Math.floor(diff / millisecondsInMinute);
	
	return { weeks, days, hours, minutes };
  }
  
  /**
   * Format time units with proper plural/singular forms
   */
  function formatTimeDifference(units: { 
	years: number, 
	months: number, 
	weeks: number, 
	days: number, 
	hours: number, 
	minutes: number 
  }): string {
	const formatUnit = (value: number, singular: string, plural: string) => {
	  if (value <= 0) return null;
	  return `${value} ${value === 1 ? singular : plural}`;
	};
  
	const parts = [
	  formatUnit(units.years, 'Year', 'Years'),
	  formatUnit(units.months, 'month', 'months'),
	  formatUnit(units.weeks, 'week', 'weeks'),
	  formatUnit(units.days, 'day', 'days'),
	  formatUnit(units.hours, 'hr', 'hrs'),
	  formatUnit(units.minutes, 'min', 'mins')
	].filter(Boolean);
	
	return parts.length > 0 ? parts.join(', ') : "Just now";
  }
  
  // Example usage:
  // console.log(calculateTimeDifference(new Date("2021-12-25T10:30:00")));
  
  // Example usage:
  // console.log(calculateTimeDifference("2021-12-25T10:30:00"));
  // Output: "3 Years, 2 months, 3 weeks, 5 days, 8 hrs, 15 mins" (depending on current date)

  /**
 * Formats a number as a Nigerian Naira currency string with comma separators
 * @param value - The number to format
 * @param options - Formatting options
 * @returns Formatted currency string
 */
function formatCurrency(
	value: number,
	options: {
	  currency?: string
	  locale?: string
	  decimals?: number
	  showCurrency?: boolean
	  currencyPosition?: "prefix" | "suffix"
	} = {},
  ): string {
	// Defaults resolve from the utility's configured currency (tenant branding),
	// with decimals following the currency's ISO precision unless overridden.
	const { currency = getTenantCurrency(), locale, decimals = getCurrencyDecimals(options.currency), showCurrency = true, currencyPosition = "prefix" } = options

	const CURRENCY_LOCALES: Record<string, string> = {
	  NGN: "en-NG",
	  ZMW: "en-ZM",
	  USD: "en-US",
	  EUR: "de-DE",
	  GBP: "en-GB",
	  JPY: "ja-JP",
	  CNY: "zh-CN",
	  INR: "en-IN",
	}
	const resolvedLocale = locale ?? CURRENCY_LOCALES[currency] ?? "en-US"

	if (value === null || value === undefined || isNaN(value)) {
	  return ""
	}

	try {
	  const formatter = new Intl.NumberFormat(resolvedLocale, {
		style: showCurrency ? "currency" : "decimal",
		currency: showCurrency ? currency : undefined,
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals,
		currencyDisplay: "symbol",
	  })
  
	  let formatted = formatter.format(value)
  
	  if (showCurrency && currencyPosition === "suffix") {
		const parts = formatter.formatToParts(value)
		const currencySymbol = parts.find((part) => part.type === "currency")?.value || getCurrencySymbol(currency)
  
		formatted = formatted.replace(currencySymbol, "").trim()
  
		formatted = `${formatted} ${currencySymbol}`
	  }
  
	  return formatted
	} catch (error) {
	  const fixed = value.toFixed(decimals)
	  const [whole, fraction] = fixed.split(".")
  
	  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  
	  const formatted = fraction ? `${withCommas}.${fraction}` : withCommas
  
	  if (showCurrency) {
		const symbol = getCurrencySymbol(currency)
		return currencyPosition === "prefix" ? `${symbol}${formatted}` : `${formatted} ${symbol}`
	  }
  
	  return formatted
	}
  }
  
  // Usage examples:
  // formatCurrency(1234567.89)                           // "₦1,234,567.89"
  // formatCurrency(1234567.89, { showCurrency: false })  // "1,234,567.89"
  // formatCurrency(1234567.89, { decimals: 0 })          // "₦1,234,568"
  // formatCurrency(1234567.89, { currencyPosition: 'suffix' }) // "1,234,567.89 ₦"
  
  

export type TransactionStatusKey = "successful" | "pending" | "failed" | "refunded" | "unknown";

const TRANSACTION_STATUS_LABELS: Record<TransactionStatusKey, string> = {
  successful: "Successful",
  pending: "Pending",
  failed: "Failed",
  refunded: "Refunded",
  unknown: "Unknown",
};

export function getTransactionStatusInfo(status: number): { key: TransactionStatusKey; label: string } {
  let key: TransactionStatusKey;
  switch (status) {
    case 2: key = "successful"; break;
    case 1: key = "pending"; break;
    case 3: key = "failed"; break;
    case 4: key = "refunded"; break;
    default: key = "unknown";
  }
  return { key, label: TRANSACTION_STATUS_LABELS[key] };
}

function formatAmount(value: number | string | null | undefined, decimals = 2): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (num === null || num === undefined || isNaN(num as number)) return "";
  return (num as number).toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// Usage: formatAmount(1234567.89) → "1,234,567.89"
//        formatAmount(1000)       → "1,000.00"
//        formatAmount(9.5, 0)     → "10"

export function getOrdinalSuffix(day: number): string {
	if (day >= 11 && day <= 13) return "th";
	switch (day % 10) {
		case 1: return "st";
		case 2: return "nd";
		case 3: return "rd";
		default: return "th";
	}
}

export function formatDateWithOrdinal(primaryDate: string, fallbackDate: string): string {
	const primaryParsed = new Date(primaryDate);
	const isInvalid = isNaN(primaryParsed.getTime()) || primaryParsed.getFullYear() <= 1;
	const date = isInvalid ? new Date(fallbackDate) : primaryParsed;
	if (isNaN(date.getTime())) return "";

	const dayName = date.toLocaleString("en-US", { weekday: "short" });
	const day = date.getDate();
	const month = date.toLocaleString("en-US", { month: "short" });
	const year = date.getFullYear();
	const hours12 = date.getHours() % 12 || 12;
	const minutes = date.getMinutes().toString().padStart(2, "0");
	const ampm = date.getHours() >= 12 ? "PM" : "AM";

	return `${dayName}, ${day}${getOrdinalSuffix(day)} ${month}, ${year}, ${hours12}:${minutes} ${ampm}`;
}

export { handleCopy, formatNumber, getOrdinalPosition, formatTime, formatTimeLocal, convertToString, isGuid, formatEnumString, calculateTimeDifference, formatCurrency, formatAmount };
